import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { ChartLineUp, Lock, TrendUp, Users, Receipt } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin conversion analytics. Shows superadmins the global funnel; tenant
 * admins see their own tenant's window.
 */
export default function ConversionAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/api/analytics/conversion?days=${days}`, { withCredentials: true })
      .then((r) => { if (active) { setData(r.data); setError(null); } })
      .catch((e) => { if (active) setError(e?.response?.data?.detail || 'Failed to load'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  return (
    <Layout>
      <div className="space-y-6 p-6" data-testid="conversion-analytics-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <ChartLineUp size={28} weight="fill" className="text-brand" /> Conversion Analytics
            </h1>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
              Paywall impressions · Trial→paid funnel · Add-on attach rates
            </p>
          </div>
          <div className="inline-flex items-center bg-muted border border-border p-1" data-testid="window-toggle">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`window-${d}`}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest ${
                  days === d ? 'bg-brand text-white' : 'text-muted-foreground'
                }`}
              >Last {d}d</button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="border border-border p-12 text-center text-muted-foreground" data-testid="ca-loading">Loading…</div>
        )}
        {error && (
          <div className="border border-rose-300 bg-rose-50 p-4 text-rose-700 text-sm" data-testid="ca-error">{error}</div>
        )}

        {data && !loading && (
          <>
            {/* Hero KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ca-kpi-row">
              <KpiTile
                icon={<Lock size={18} weight="fill" />}
                label="Paywall views"
                value={(data.paywall_impressions?.total || 0).toLocaleString()}
                accent="amber"
              />
              <KpiTile
                icon={<Users size={18} weight="fill" />}
                label="Trial signups"
                value={(data.trial_to_paid?.trial_signups_in_window || 0).toLocaleString()}
                accent="brand"
              />
              <KpiTile
                icon={<Receipt size={18} weight="fill" />}
                label="Paid conversions"
                value={(data.trial_to_paid?.paid_in_window || 0).toLocaleString()}
                accent="emerald"
              />
              <KpiTile
                icon={<TrendUp size={18} weight="fill" />}
                label="Conversion %"
                value={`${data.trial_to_paid?.conversion_pct ?? 0}%`}
                accent={(data.trial_to_paid?.conversion_pct ?? 0) >= 5 ? 'emerald' : 'rose'}
              />
            </div>

            {/* Top features driving paywall impressions */}
            <Section title="Top paywall features" subtitle="Which locked features drive the most upgrade interest">
              <div className="border border-border" data-testid="ca-top-features">
                {data.paywall_impressions?.top_features?.length ? (
                  data.paywall_impressions.top_features.map((row, i) => {
                    const max = data.paywall_impressions.top_features[0].count || 1;
                    const pct = Math.round((row.count / max) * 100);
                    return (
                      <div key={row.feature} className="flex items-center gap-3 px-4 py-2 border-b border-border last:border-b-0" data-testid={`ca-feature-${row.feature}`}>
                        <span className="text-[10px] font-mono w-6 text-muted-foreground">{i + 1}.</span>
                        <span className="font-mono uppercase text-xs flex-1">{row.feature.replace(/_/g, ' ')}</span>
                        <div className="hidden md:block flex-1 h-2 bg-slate-200">
                          <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-sm font-bold tabular-nums w-12 text-right">{row.count}</span>
                      </div>
                    );
                  })
                ) : <Empty msg="No paywall impressions in this window — Starter users haven't hit a locked feature yet." />}
              </div>
            </Section>

            {/* By tier */}
            <Section title="Paywalls by current tier" subtitle="Which tier's users see the most paywalls (= biggest upgrade opportunity)">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ca-by-tier">
                {data.paywall_impressions?.by_tier?.length ? (
                  data.paywall_impressions.by_tier.map((row) => (
                    <div key={row.plan_key} className="border border-border p-3" data-testid={`ca-tier-${row.plan_key}`}>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{row.plan_key}</div>
                      <div className="text-2xl font-bold tabular-nums mt-1">{row.count.toLocaleString()}</div>
                    </div>
                  ))
                ) : <Empty msg="No data" />}
              </div>
            </Section>

            {/* Add-on attach rates (superadmin) */}
            {data.scope === 'global' && data.addon_attach_rates?.length > 0 && (
              <Section title="Add-on attach rates" subtitle="% of active companies that have each add-on enabled">
                <div className="border border-border overflow-x-auto" data-testid="ca-addon-attach">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-2 font-bold uppercase tracking-widest">Add-on</th>
                        <th className="text-right p-2 font-bold uppercase tracking-widest">Price</th>
                        <th className="text-right p-2 font-bold uppercase tracking-widest">Active</th>
                        <th className="text-left p-2 font-bold uppercase tracking-widest">Attach %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.addon_attach_rates.map((a) => (
                        <tr key={a.addon_key} className="border-t border-border" data-testid={`ca-addon-${a.addon_key}`}>
                          <td className="p-2 font-medium">{a.name}</td>
                          <td className="p-2 text-right tabular-nums">${a.price_monthly_usd}</td>
                          <td className="p-2 text-right tabular-nums font-bold">{a.active_count}</td>
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-slate-200 max-w-[200px]">
                                <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, a.attach_pct)}%` }} />
                              </div>
                              <span className="font-mono tabular-nums text-[11px] w-12 text-right">{a.attach_pct}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Plan distribution (superadmin) */}
            {data.scope === 'global' && data.plan_distribution?.length > 0 && (
              <Section title="Plan distribution" subtitle="Companies on each tier × status">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ca-plan-dist">
                  {data.plan_distribution.map((row) => (
                    <div key={`${row.plan_key}_${row.status}`} className="border border-border p-3">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        {row.plan_key} · {row.status}
                      </div>
                      <div className="text-2xl font-bold tabular-nums mt-1">{row.count}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Lifecycle events */}
            {Object.keys(data.lifecycle_events || {}).length > 0 && (
              <Section title="Lifecycle events" subtitle="Subscription health signals from Stripe webhooks">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="ca-lifecycle">
                  {Object.entries(data.lifecycle_events).map(([type, count]) => (
                    <div key={type} className="border border-border p-3">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{type.replace(/\./g, ' › ')}</div>
                      <div className="text-2xl font-bold tabular-nums mt-1">{count}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

const KpiTile = ({ icon, label, value, accent }) => {
  const accentClass = {
    brand: 'border-brand text-brand',
    emerald: 'border-emerald-600 text-emerald-700',
    amber: 'border-amber-500 text-amber-700',
    rose: 'border-rose-500 text-rose-700',
  }[accent] || 'border-border text-foreground';
  return (
    <div className={`border-l-4 ${accentClass} bg-card border-y border-r border-border p-4`} data-testid={`ca-kpi-${label.toLowerCase().replace(/\s+/g, '-').replace(/%/g, 'pct')}`}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</span>
      </div>
      <div className="text-3xl font-bold mt-2 tabular-nums">{value}</div>
    </div>
  );
};

const Section = ({ title, subtitle, children }) => (
  <div className="space-y-2">
    <div>
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const Empty = ({ msg }) => <div className="p-6 text-center text-xs text-muted-foreground">{msg}</div>;
